import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Linking,
  TouchableOpacity,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useAppDispatch } from '@/store';
import { 
  setPayingWithSourceId,
  setPayingForResidentId,
  setIsPaymentModalOpen,
} from '@/store/slices/residents/paymentsSlice';

type Params = {
  leaseId: number;
  residentId?: number;
};

export const TransactionTimeoutScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { colors, semantic } = useThemeColors();
  const DESKTOP_COLORS = useDesktopColors();

  const { leaseId, residentId } = (route.params || {}) as Params;

  const resetPaymentState = () => {
    dispatch(setPayingWithSourceId(0));
    dispatch(setPayingForResidentId(0));
    dispatch(setIsPaymentModalOpen(false));
  };

  const handleClose = () => {
    resetPaymentState();
    // Navigate back to payments screen
    navigation.replace('Payments');
  };

  const handleContactSupport = () => {
    const subject = 'Payment Timeout';
    const body = `Hello Resio Support,

I experienced a payment timeout while trying to make a payment for lease ID: ${leaseId}.

Please help me check the status of my payment.

Thank you.`;
    
    const mailtoUrl = `mailto:support@resio.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    Linking.openURL(mailtoUrl).catch(() => {
      // Fallback if email app is not available
      Linking.openURL('https://resio.com/contact');
    });
  };

  const footerElement = (
    <View style={styles.footerRow}>
      <ModalButton label="Close" variant="primary" onPress={handleClose} />
    </View>
  );

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="large"
      onClose={handleClose}
      title="Transaction Pending"
      scrollable
      footer={footerElement}
    >
      <View style={styles.header}>
        <TimeoutIcon />
      </View>

      <View style={styles.messageSection}>
        <Text style={[styles.mainMessage, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>
          Your transaction's status is unknown.
        </Text>

        <Text style={[styles.description, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
          Our payment processing system is experiencing a delay, and it is unknown
          whether your payment will be posted yet.
        </Text>

        <Text style={[styles.instructions, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>
          To monitor the status of your payment, please check the Transaction History
          table under your lease. If it hasn't appeared in 15 minutes or more, please
          contact support. We apologize for the delay.
        </Text>
      </View>

      <View style={[styles.supportSection, { backgroundColor: colors.activityIcon.bg }]}>
        <SupportIcon />
        <Text style={[styles.supportText, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>
          Contact Resio Support
        </Text>
        <TouchableOpacity style={[styles.supportButton, { borderColor: colors.card.border }]} onPress={handleContactSupport}>
          <Text style={[styles.supportButtonText, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>Email Support</Text>
        </TouchableOpacity>
        <Text style={[styles.supportEmail, { color: DESKTOP_COLORS.BUTTON_PRIMARY }]}>
          support@resio.com
        </Text>
      </View>

      <View style={styles.statusSection}>
        <Text style={[styles.statusTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>What to do next:</Text>

        <View style={styles.statusItem}>
          <StepIcon step="1" />
          <View style={styles.statusContent}>
            <Text style={[styles.statusText, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
              Check your Transaction History in 15 minutes
            </Text>
          </View>
        </View>

        <View style={styles.statusItem}>
          <StepIcon step="2" />
          <View style={styles.statusContent}>
            <Text style={[styles.statusText, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
              Contact support if payment doesn't appear
            </Text>
          </View>
        </View>

        <View style={styles.statusItem}>
          <StepIcon step="3" />
          <View style={styles.statusContent}>
            <Text style={[styles.statusText, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
              Keep sufficient funds in your account
            </Text>
          </View>
        </View>
      </View>
    </UnifiedModal>
  );
};

// Helper Icons
const TimeoutIcon: React.FC = () => {
  const { colors, semantic } = useThemeColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CLOCK;
  return Icon ? (
    <Icon width={64} height={64} color={semantic.status.warning} />
  ) : (
    <View style={[styles.timeoutIconFallback, { backgroundColor: semantic.status.warning }]}>
      <Text style={[styles.timeoutIconText, { color: semantic.text.inverse }]}>⏱</Text>
    </View>
  );
};

const SupportIcon: React.FC = () => {
  const DESKTOP_COLORS = useDesktopColors();
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHAT_BUBBLE_LEFT_RIGHT;
  return Icon ? (
    <Icon width={24} height={24} color={DESKTOP_COLORS.BUTTON_PRIMARY} />
  ) : null;
};

const StepIcon: React.FC<{ step: string }> = ({ step }) => {
  const { semantic } = useThemeColors();
  const DESKTOP_COLORS = useDesktopColors();
  return (
    <View style={[styles.stepIcon, { backgroundColor: DESKTOP_COLORS.BUTTON_PRIMARY }]}>
      <Text style={[styles.stepIconText, { color: semantic.text.inverse }]}>{step}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  timeoutIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  timeoutIconText: {
    fontSize: 32,
  },
  messageSection: {
    marginBottom: 32,
  },
  mainMessage: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 16,
  },
  instructions: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  supportSection: {
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    marginBottom: 32,
  },
  supportText: {
    fontSize: 16,
    fontWeight: '600',
    marginVertical: 12,
  },
  supportButton: {
    marginVertical: 8,
    minWidth: 150,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  supportButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  supportEmail: {
    fontSize: 14,
    marginTop: 8,
  },
  statusSection: {
    marginBottom: 32,
  },
  statusTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 20,
  },
  statusItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  stepIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  stepIconText: {
    fontSize: 14,
    fontWeight: '700',
  },
  statusContent: {
    flex: 1,
    paddingTop: 6,
  },
  statusText: {
    fontSize: 14,
    lineHeight: 20,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
});

export default TransactionTimeoutScreen;
